import type { TFunction } from "i18next";
import { useWatch } from "react-hook-form";

import type { CampaignJobBoardFormControl } from "./types";
import { MAX_MONTHLY_BUDGET } from "../../constants";

import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";

interface CurrentMonthBudgetFieldProps {
  control: CampaignJobBoardFormControl;
  useDifferentCurrentMonthBudget: boolean;
  showCurrentMonthBudget: boolean;
  daysRemainingInMonth: number | null;
  monthlyBudget?: number;
  t: TFunction;
}

export function CurrentMonthBudgetField({
  control,
  useDifferentCurrentMonthBudget,
  showCurrentMonthBudget,
  daysRemainingInMonth,
  monthlyBudget,
  t,
}: CurrentMonthBudgetFieldProps) {
  // Watch monthlyBudget to calculate 200% limit dynamically
  // Must be called before any early returns (rules of hooks)
  const watchedMonthlyBudget = useWatch({
    control,
    name: "monthlyBudget",
  });

  if (!showCurrentMonthBudget || daysRemainingInMonth === null) {
    return null;
  }
  const effectiveMonthlyBudget = watchedMonthlyBudget ?? monthlyBudget ?? 0;
  const maxAllowed = effectiveMonthlyBudget > 0 ? effectiveMonthlyBudget * 2 : MAX_MONTHLY_BUDGET;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <FormField
        control={control}
        name="useDifferentCurrentMonthBudget"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start space-x-3 mb-1">
              <Checkbox
                id="useDifferentCurrentMonthBudget"
                checked={field.value ?? false}
                onCheckedChange={(checked) => {
                  field.onChange(checked === true);
                }}
                className="mt-0.5"
              />
              <label
                htmlFor="useDifferentCurrentMonthBudget"
                className="text-sm text-foreground cursor-pointer leading-relaxed"
              >
                {t("campaignJobBoard.differentBudgetForCurrentMonth", {
                  count: daysRemainingInMonth,
                })}
              </label>
            </div>
            <FormDescription className="ml-7">
              {t(
                "admin.campaigns.jobBoard.useDifferentCurrentMonthBudgetDescription",
                {
                  defaultValue:
                    "Set a custom budget for this month only, useful when starting mid-month.",
                }
              )}
            </FormDescription>
          </FormItem>
        )}
      />

      {useDifferentCurrentMonthBudget && (
        <FormField
          control={control}
          name="currentMonthBudget"
          render={({ field }) => (
            <FormItem>
              <span className="text-sm font-medium text-foreground">{`${t(
                "common.current"
              )} ${t("common.month")} ${t("common.budget")}`}</span>
              <FormControl>
                <FormattedCurrencyInput
                  value={field.value ?? undefined}
                  onChange={(value) => field.onChange(value ?? null)}
                  maxValue={maxAllowed}
                  placeholder={effectiveMonthlyBudget > 0 ? `${effectiveMonthlyBudget}` : "0"}
                />
              </FormControl>
              <FormDescription>
                {t("admin.campaigns.jobBoard.currentMonthBudgetDescription", {
                  defaultValue:
                    "Prorated budget for the remaining {{count}} days of the current month.",
                  count: daysRemainingInMonth,
                })}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
